import { useState } from "react";
import { CalendarDays, Clock, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

const STATUS_STYLES = {
  pending: "bg-amber-500/15 text-amber-400 border-amber-500/40",
  approved: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40",
  declined: "bg-red-500/15 text-red-400 border-red-500/40",
  rescheduled: "bg-sky-500/15 text-sky-400 border-sky-500/40",
  quote: "bg-[#1e9bff]/15 text-[#1e9bff] border-[#1e9bff]/40",
};

export default function BookingsTab({ bookings, reload }) {
  const [resched, setResched] = useState(null); // booking id
  const [form, setForm] = useState({ date: "", slot: "" });

  const patch = async (id, body) => {
    try {
      await api.patch(`/admin/bookings/${id}`, body);
      toast.success("Booking updated");
      setResched(null);
      reload();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (bookings.length === 0)
    return <p className="text-sm text-[#93b4d4] py-10 text-center" data-testid="bookings-empty">No bookings yet — they'll appear here as they come in.</p>;

  return (
    <div className="space-y-4" data-testid="bookings-tab">
      {bookings.map((b) => (
        <article key={b.id} className="p-5 rounded-2xl bg-[#0b1322] border border-[#1e9bff]/20" data-testid={`booking-row-${b.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div>
              <p className="text-white font-semibold">{b.name} <span className="text-[#627d98] font-mono-lbl text-xs">#{b.id.slice(0, 8).toUpperCase()}</span></p>
              <p className="text-xs text-[#93b4d4] font-mono-lbl">{b.services.join(", ")} · {b.property_type}</p>
            </div>
            <span className={`px-2.5 py-1 rounded-full border text-[10px] font-mono-lbl uppercase tracking-wider ${STATUS_STYLES[b.status] || ""}`} data-testid={`booking-status-${b.id}`}>
              {b.status}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-[#c8d9ea] font-mono-lbl mb-4">
            <span className="flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-[#1e9bff]" /> {b.date || "TBA"}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-[#1e9bff]" /> {b.slot_label || "TBA"}</span>
            <a href={`tel:${b.phone}`} className="flex items-center gap-1.5 text-[#1e9bff] hover:underline"><Phone className="w-3.5 h-3.5" /> {b.phone}</a>
            <a href={`mailto:${b.email}`} className="flex items-center gap-1.5 text-[#1e9bff] hover:underline truncate"><Mail className="w-3.5 h-3.5 shrink-0" /> {b.email}</a>
          </div>
          {(b.address || b.postcode) && <p className="text-xs text-[#93b4d4] mb-2">{b.address} {b.postcode}</p>}
          {b.notes && <p className="text-xs text-[#93b4d4] mb-3 italic">&ldquo;{b.notes}&rdquo;</p>}

          {resched === b.id ? (
            <div className="flex flex-wrap gap-2 items-center pt-3 border-t border-white/10">
              <input type="date" value={form.date} data-testid={`reschedule-date-${b.id}`}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="bg-[#070b12] border border-white/15 rounded-lg px-3 py-2 text-xs text-white [color-scheme:dark]" />
              <input type="time" value={form.slot} data-testid={`reschedule-time-${b.id}`}
                onChange={(e) => setForm({ ...form, slot: e.target.value })}
                className="bg-[#070b12] border border-white/15 rounded-lg px-3 py-2 text-xs text-white [color-scheme:dark]" />
              <button onClick={() => patch(b.id, { date: form.date, slot: form.slot || null })} data-testid={`reschedule-save-${b.id}`}
                className="px-4 py-2 rounded-lg bg-[#1e9bff] text-black text-xs font-bold uppercase disabled:opacity-40" disabled={!form.date}>
                Save
              </button>
              <button onClick={() => setResched(null)} className="px-4 py-2 rounded-lg border border-white/15 text-xs text-[#93b4d4]">Cancel</button>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2 pt-3 border-t border-white/10">
              <button onClick={() => patch(b.id, { status: "approved" })} data-testid={`booking-approve-${b.id}`}
                className="px-4 py-2 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 text-xs font-mono-lbl uppercase hover:bg-emerald-500/30 transition-colors">
                Approve
              </button>
              <button onClick={() => { setResched(b.id); setForm({ date: b.date || "", slot: b.slot_label || b.slot || "" }); }} data-testid={`booking-reschedule-${b.id}`}
                className="px-4 py-2 rounded-lg bg-sky-500/20 border border-sky-500/40 text-sky-400 text-xs font-mono-lbl uppercase hover:bg-sky-500/30 transition-colors">
                Reschedule
              </button>
              <button onClick={() => patch(b.id, { status: "declined" })} data-testid={`booking-decline-${b.id}`}
                className="px-4 py-2 rounded-lg bg-red-500/20 border border-red-500/40 text-red-400 text-xs font-mono-lbl uppercase hover:bg-red-500/30 transition-colors">
                Decline
              </button>
            </div>
          )}
        </article>
      ))}
    </div>
  );
}
